// 화면 효과: 짧은 비네트·색 펄스. 화면 전체를 덮는 사각 한 장(드로우콜 1)이 모든 펄스를 합쳐 한 번에 그린다.
// 안전: 섬광 규칙(AGENTS.md 연출 등급, FLASH_SAFETY): 1초에 3회 이하, 간격 334ms 이상. 밝기 변화는 최소 40ms에 걸쳐 올라가고 최대 불투명도는 SCREEN_MAX_ALPHA로 막는다.
// 세기 SOFT_PULSE 이하의 옅은 비네트는 섬광으로 세지 않는다. reduceFlash(게임 설정)면 세기를 40%로 줄인다.
// 쓰는 칸: color = 펄스 색, intensity = 최대 세기(0..1, 불투명도), life = 길이(초), variant 0 = 가장자리 비네트 / 1 = 가장자리 + 전체 색조. 위치 칸은 쓰지 않는다.
import * as THREE from 'three/webgpu';
import { clamp, float, length as vlength, mix, positionGeometry, smoothstep, step, uniform, vec2, vec4 } from 'three/tsl';
import { FLASH_SAFETY } from '../../shared/config';
import { clamp01 } from './path';
import { MODULE_CAPS, type FxModule, type FxParams, type ModuleKind } from './types';

/** 화면에 더해지는 최대 불투명도 */
export const SCREEN_MAX_ALPHA = 0.3;
/** 이 세기 이하의 비네트는 섬광 횟수에 세지 않는다 */
export const SOFT_PULSE = 0.12;
/** 섬광 약화 배율 */
export const REDUCE_GAIN = 0.4;
/** 밝기가 올라가는 최소 시간(초): 갑작스러운 번쩍임 금지 */
const MIN_ATTACK = 0.04;

/**
 * 공용 섬광 예산(렌더가 각성·처형 섬광과 같은 FlashLimiter를 꽂는다). 있으면 화면 펄스의 섬광 판정은 이것만 쓴다.
 * 없으면(프리뷰·단위 테스트) 이 모듈의 자체 한도를 쓴다.
 */
export interface FlashGate { admit(): boolean }

class Pulse {
  age = 0; life = 1; peak = 0; r = 1; g = 1; b = 1; tint = 0;
}

const tmpColor = new THREE.Color();

export class ScreenModule implements FxModule {
  readonly kind: ModuleKind = 'screen';
  readonly group = new THREE.Group();
  readonly capacity: number;
  /** 승인·거부된 섬광 수(QA) */
  granted = 0;
  blocked = 0;
  private readonly slots: Pulse[] = [];
  private n = 0;
  private readonly mesh: THREE.Mesh;
  private readonly uColor = uniform(new THREE.Color(1, 1, 1));
  private readonly uVig = uniform(0);
  private readonly uTint = uniform(0);
  /** 이 모듈이 흘린 시간(초): 섬광 한도의 기준 시계 */
  private clock = 0;
  private readonly grants = new Float64Array(8);
  private grantN = 0;
  private lastGrant = -1e9;
  private reduce = false;
  private gate: FlashGate | null = null;

  constructor(cap = MODULE_CAPS.screen) {
    this.capacity = cap;
    for (let i = 0; i < cap; i++) this.slots.push(new Pulse());
    const m = new THREE.MeshBasicNodeMaterial();
    m.transparent = true;
    m.depthTest = false;
    m.depthWrite = false;
    m.side = THREE.DoubleSide;
    m.forceSinglePass = true; // 양면 투명이 2패스로 그려지면 drawCalls 집계(1)와 어긋난다
    m.fog = false;
    // 화면 전체: 지오메트리의 xy(-1..1)를 그대로 클립 공간에 쓴다(카메라와 무관)
    m.vertexNode = vec4(positionGeometry.xy, 0, 1);
    // c013: 비네트를 네 귀퉁이 쪽으로 좁힌다. 이전(0.6~1.3, 위아래 같은 가중)은 화면 위쪽 가운데까지 붉게 물들여(낙화 착지 0.2) 화면 전체를 씻는 듯 보였다.
    // 위쪽(배경·하늘)은 가중을 0.78로 낮춰 위 가운데는 0, 위 귀퉁이는 약 0.6, 아래 귀퉁이는 약 0.95 — 가장자리 틀로만 읽힌다
    const gy = mix(float(1.0), float(0.78), step(0, positionGeometry.y));
    const d = vlength(positionGeometry.xy.mul(vec2(0.92, gy)));
    const vig = smoothstep(0.92, 1.4, d);
    m.colorNode = this.uColor;
    m.opacityNode = clamp(vig.mul(this.uVig).add(this.uTint), 0, SCREEN_MAX_ALPHA);
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), m);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 1000;
    this.mesh.visible = false;
    this.mesh.name = 'fx-screen';
    this.group.name = 'fx-screen';
    this.group.add(this.mesh);
  }

  get activeCount(): number { return this.n; }
  get drawCalls(): number { return this.mesh.visible ? 1 : 0; }
  setReduceFlash(on: boolean): void { this.reduce = on; }
  /** 공용 섬광 예산을 꽂는다(null이면 자체 한도). Codex P1(c013): 화면 펄스와 각성·처형 섬광이 각자 한도를 가져 같은 1초에 합쳐 넘을 수 있었다 */
  setGate(gate: FlashGate | null): void { this.gate = gate; }

  /** 섬광 한도: 1초 3회 이하 · 간격 334ms 이상. 허용하면 기록한다 */
  private grant(): boolean {
    if (this.clock - this.lastGrant < FLASH_SAFETY.minGapMs / 1000) return false;
    let recent = 0;
    for (let i = 0; i < this.grants.length; i++) if (this.grants[i] > 0 && this.clock - this.grants[i] < 1) recent++;
    if (recent >= FLASH_SAFETY.maxPerSecond) return false;
    this.grants[this.grantN++ % this.grants.length] = this.clock + 1e-9;
    this.lastGrant = this.clock;
    return true;
  }

  spawn(p: Readonly<FxParams>): boolean {
    const peak = Math.min(SCREEN_MAX_ALPHA, Math.max(0, p.intensity)) * (this.reduce ? REDUCE_GAIN : 1);
    if (p.intensity > SOFT_PULSE) {
      if (!(this.gate !== null ? this.gate.admit() : this.grant())) { this.blocked++; return false; }
      this.granted++;
    }
    let i: number;
    if (this.n < this.capacity) i = this.n++;
    else {
      // 가득 참: 가장 오래된 펄스를 덮어쓴다
      i = 0;
      let best = -1;
      for (let k = 0; k < this.n; k++) { const f = this.slots[k].age / this.slots[k].life; if (f > best) { best = f; i = k; } }
    }
    const s = this.slots[i];
    s.age = Math.max(0, Math.min(0.2, p.age0));
    s.life = Math.max(0.1, p.life);
    s.peak = peak;
    s.tint = p.variant >= 0.5 ? 0.12 : 0;
    tmpColor.setHex(p.color);
    s.r = tmpColor.r; s.g = tmpColor.g; s.b = tmpColor.b;
    this.compose();
    return true;
  }

  /** 포락선: 앞 구간(최소 40ms)에서 올라가고 뒤로 부드럽게 줄어든다 */
  private env(s: Pulse): number {
    const u = clamp01(s.age / s.life);
    const atk = Math.max(0.18, MIN_ATTACK / s.life);
    return u < atk ? u / atk : Math.pow(1 - (u - atk) / (1 - atk), 1.6);
  }

  /** 살아 있는 펄스를 합쳐 uniform에 쓴다(할당 없음) */
  private compose(): void {
    let vig = 0, tint = 0, wr = 0, wg = 0, wb = 0, wsum = 0;
    for (let i = 0; i < this.n; i++) {
      const s = this.slots[i];
      const e = this.env(s) * s.peak;
      if (e > vig) vig = e;
      const t = e * s.tint;
      if (t > tint) tint = t;
      wr += s.r * e; wg += s.g * e; wb += s.b * e; wsum += e;
    }
    const inv = wsum > 1e-6 ? 1 / wsum : 0;
    (this.uColor.value as THREE.Color).setRGB(wr * inv, wg * inv, wb * inv);
    this.uVig.value = Math.min(SCREEN_MAX_ALPHA, vig);
    this.uTint.value = Math.min(SCREEN_MAX_ALPHA, tint);
    this.mesh.visible = this.n > 0 && (vig > 1e-4 || tint > 1e-4);
  }

  update(dt: number): void {
    this.clock += dt;
    if (this.n === 0) return;
    let i = 0;
    while (i < this.n) {
      const s = this.slots[i];
      s.age += dt;
      if (s.age >= s.life) {
        const j = --this.n;
        if (i !== j) { this.slots[i] = this.slots[j]; this.slots[j] = s; }
        continue;
      }
      i++;
    }
    this.compose();
  }

  clear(): void {
    this.n = 0;
    this.compose();
  }

  /** 섬광 한도 기록(기준 시계·승인 이력·마지막 승인 시각)과 QA 집계를 처음 상태로 되돌린다. 캡처처럼 샘플마다 독립된 장면을 찍을 때 앞 샘플의 한도가 이월되지 않게 한다 */
  resetLimiter(): void {
    this.clock = 0;
    this.grants.fill(0);
    this.grantN = 0;
    this.lastGrant = -1e9;
    this.granted = 0;
    this.blocked = 0;
  }

  prewarm(on: boolean): void {
    if (on) {
      this.uVig.value = 0;
      this.uTint.value = 0;
      this.mesh.visible = true;
    } else this.compose();
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.group.remove(this.mesh);
    this.n = 0;
  }
}
